/**
 * StatusBadge — state-based pill. One place that maps a transaction
 * state name to its human label + color class. Layouts render this
 * when no urgency tag is active.
 *
 * Consolidates the three parallel status-label maps that used to live
 * inline in RenterDashboardPage, ProviderDashboardPage, and the old
 * BookingsPage BookingItem — each subtly different, each covering a
 * different subset of states.
 */
import React from 'react';

import { NEGOTIATION_PROCESS_NAME } from '../../transactions/transaction';
import css from './TransactionCard.module.css';

// Map resolved process state → display text + class. Covers both the
// event-kit-rental and default-booking processes (see state lists in
// RenterDashboardPage.js / BookingsPage.js for the full rationale).
const STATE_TO_BADGE = {
  // Pre-pickup / accepted
  accepted: { text: 'Confirmed', cls: 'statusConfirmed' },
  'pickup-pending': { text: 'Pickup pending', cls: 'statusConfirmed' },
  'pickup-provider-confirmed': { text: 'Pickup pending', cls: 'statusConfirmed' },
  'pickup-renter-confirmed': { text: 'Pickup pending', cls: 'statusConfirmed' },

  // Active
  'picked-up': { text: 'Active', cls: 'statusActive' },
  'return-pending': { text: 'Active', cls: 'statusActive' },
  'active-rental': { text: 'Active', cls: 'statusActive' },

  // Terminal success
  completed: { text: 'Completed', cls: 'statusCompleted' },
  reviewed: { text: 'Completed', cls: 'statusCompleted' },
  'return-confirmed-pending-release': { text: 'Returned', cls: 'statusCompleted' },
  delivered: { text: 'Completed', cls: 'statusCompleted' },

  // Terminal cancel
  'cancelled-by-customer': { text: 'Cancelled', cls: 'statusCancelled' },
  'cancelled-by-provider': { text: 'Cancelled', cls: 'statusCancelled' },
  'cancelled-by-customer-pickup-issue': { text: 'Cancelled', cls: 'statusCancelled' },
  'cancelled-pickup-refund-denied': { text: 'Cancelled', cls: 'statusCancelled' },
  canceled: { text: 'Cancelled', cls: 'statusCancelled' },
  declined: { text: 'Declined', cls: 'statusCancelled' },
  expired: { text: 'Expired', cls: 'statusCancelled' },
  'payment-expired': { text: 'Expired', cls: 'statusCancelled' },

  // Attention
  'return-late': { text: 'Return overdue', cls: 'statusAttention' },
  disputed: { text: 'Needs attention', cls: 'statusAttention' },
  'damage-reported': { text: 'Needs attention', cls: 'statusAttention' },
  // v10 review-first refunds: booking on hold pending Merrily review
  'pickup-issue-review': { text: 'Refund under review', cls: 'statusAttention' },

  // Pending acceptance (default-booking preauthorized-style)
  preauthorized: { text: 'Pending', cls: 'statusConfirmed' },
  'pending-payment': { text: 'Pending payment', cls: 'statusConfirmed' },
  inquiry: { text: 'Inquiry', cls: 'statusConfirmed' },

  // Custom-quote / negotiation requests (renter-facing). "Quote requested"
  // is awaiting the host's offer (neutral); "Offer received" means the host
  // has sent/revised an offer and it's the renter's turn (attention);
  // "Counter sent" is the renter's counter awaiting the host (neutral).
  'quote-requested': { text: 'Quote requested', cls: 'statusConfirmed' },
  'offer-pending': { text: 'Offer received', cls: 'statusAttention' },
  'update-pending': { text: 'Offer received', cls: 'statusAttention' },
  'customer-offer-pending': { text: 'Counter sent', cls: 'statusConfirmed' },

  // Custom-quote accepted order (renter-facing), once the offer is paid
  // and moving to delivery. `delivered` is remapped per-process to
  // "Delivered" for negotiation via PROCESS_STATE_OVERRIDES below (booking
  // `delivered` keeps the shared "Completed" above); `changes-requested`
  // is an active redelivery loop.
  'offer-accepted': { text: 'Confirmed', cls: 'statusConfirmed' },
  'changes-requested': { text: 'Changes requested', cls: 'statusActive' },
};

// Per-process overrides for the few state names that collide across
// processes with different meanings. Keyed [processName][state]; a hit
// here wins over STATE_TO_BADGE, everything else falls through to it.
// StatusBadge stays process-blind unless a caller passes `processName`
// (from useTransactionCardData) — the shared map remains the default.
//
// default-negotiation `delivered`: the host delivered the deliverable
// and the RENTER must still accept it (the negotiation process lists
// 'delivered' in statesNeedingCustomerAttention). It's an active order,
// not a finished one, so the booking meaning "Completed" is wrong here.
// Show "Delivered" with the active (orange) treatment.
const PROCESS_STATE_OVERRIDES = {
  [NEGOTIATION_PROCESS_NAME]: {
    delivered: { text: 'Delivered', cls: 'statusActive' },
  },
};

// Visually distinct fallback for unmapped states. Renders gray with
// the literal state name + a dashed border so QA notices it instead
// of seeing "Pending" silently substituted for a real state we forgot
// to map. dev/staging logs the unmapped state to console.error so
// it's caught before users see it. Same fail-loud pattern as
// InboxPage.getPillVariant.
const UNKNOWN_BADGE_CLS = 'statusUnknown';

// Negotiation turn-taking states read differently per side: the shared
// map is renter-phrased ("Offer received" = the HOST sent an offer).
// Host card surfaces pass role="provider" so the same states read from
// the host's seat — whose turn it is drives the attention treatment.
const HOST_STATE_OVERRIDES = {
  'quote-requested': { text: 'New quote request', cls: 'statusAttention' },
  'customer-offer-pending': { text: 'Counter received', cls: 'statusAttention' },
  'offer-pending': { text: 'Offer sent', cls: 'statusConfirmed' },
  'update-pending': { text: 'Offer sent', cls: 'statusConfirmed' },
};

const StatusBadge = ({ state, processName, role, className }) => {
  const badge =
    (role === 'provider' && HOST_STATE_OVERRIDES[state]) ||
    (processName && PROCESS_STATE_OVERRIDES[processName]?.[state]) ||
    STATE_TO_BADGE[state];
  if (!badge) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(
        `[StatusBadge] Unknown state ${JSON.stringify(state)}. Add it to STATE_TO_BADGE in StatusBadge.js.`
      );
    }
    return (
      <span
        className={`${css.statusBadge} ${css[UNKNOWN_BADGE_CLS]} ${className || ''}`}
        data-state={state}
      >
        {state || 'Unknown'}
      </span>
    );
  }
  return (
    <span
      className={`${css.statusBadge} ${css[badge.cls]} ${className || ''}`}
      data-state={state}
    >
      {badge.text}
    </span>
  );
};

export default StatusBadge;
